import { useState, useEffect, useCallback } from 'react';
import { useGlobalContext } from '../../contexts';

import { Markdown, Svg, Image, Link, ContentModal } from '../../snippets';

export function AccordionPlus({ accordion, accordionSettings, areOpen, popUp }) {
  const {
    actions: { openModal },
  } = useGlobalContext();
  
  const { body, defaultOpen, header, images } = accordion;
  const [isOpen, setIsOpen] = useState(defaultOpen || areOpen);
  useEffect(() => {
    setIsOpen(defaultOpen || areOpen);
  }, [areOpen, defaultOpen]);

  const itemsPerRow = accordionSettings?.itemsPerRow;
  const isImageSizeEmpty = !accordionSettings?.imageSize;
  const imageSize = accordionSettings?.imageSize;
  const style = accordionSettings?.imageStyle;

  const handlePopUpClick = useCallback((linkText) => {
    openModal(
      <ContentModal
        content={popUp}
        linkText={linkText}
      />
    );
  }, [popUp]);

  return (
    <div>
      <button
        aria-label={`${isOpen ? 'Close' : 'Open'} accordion for ${header}`}
        className="flex shadow-hairline min-h-[4rem] w-full items-center justify-between gap-x-4 px-4 py-3 text-left xs:px-6"
        onClick={() => setIsOpen(!isOpen)}
        style={{ backgroundColor: accordionSettings?.headerBgColor, color: accordionSettings?.headerTextColor }}
        type="button"
      >
        <h3 className="text-title-h6 flex-1">{header}</h3>

        {isOpen ? (
          <Svg
            className="w-4 text-current"
            src="/svgs/minus.svg#minus"
            alt="Collapse Accordion Icon"
            title="Minus"
            viewBox="0 0 24 24"
          />
        ) : (
          <Svg
            className="w-4 text-current"
            src="/svgs/plus.svg#plus"
            alt="Expand Accordion Icon"
            title="Plus"
            viewBox="0 0 24 24"
          />
        )}
      </button>

      <div className={`pt-4 ${isOpen ? 'block' : 'hidden'}`}>
        <Markdown>{body}</Markdown>
        {images && images.length > 0 && (
          <ul className={`mx-auto grid ${itemsPerRow} ${!isImageSizeEmpty ? 'gap-4' : ''}`}>
            {images?.slice(0, 64).map((item, index) => {
              return item?.showPopUp ? (
                <li 
                  key={index}
                  onClick={() => handlePopUpClick(item?.link?.text)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handlePopUpClick(item?.link?.text);
                    }
                  }}
                  aria-label={`Open ${item?.link?.text} pop-up to subscribe to notifications`}
                  className={`relative group ${style === 'horizontal' ? 'flex flex-col items-center shadow-hairline p-2 sm:py-4 sm:flex-row' : ''}`}
                >
                <div className={`pointer-events-none absolute left-0 right-0 top-0 w-full h-full flex ${style === 'horizontal' ? 'justify-end items-baseline' : 'justify-center items-center xl:p-4'}`}>
                    <div className="flex">
                      <div className="flex flex-wrap items-center bg-primary shadow-hairline-primary text-white text-xs uppercase py-0.5 px-1.5 group-hover:bg-white group-hover:text-primary md:text-sm">
                        {popUp?.badgeText}
                        <Svg
                          className="text-white w-3 ml-1 md:w-4 group-hover:text-primary group-hover:motion-safe:animate-bounce"
                          src="/svgs/notifications.svg#notifications"
                          alt="Notification Icon"
                          title="Notification Icon"
                          viewBox="0 0 24 24"
                        />
                      </div>
                    </div>
                  </div>  
                  <div className={`${isImageSizeEmpty ? 'bg-offWhite aspect-square' : ''}`}>
                    <div
                      {...(!isImageSizeEmpty ? { className: 'w-full h-full flex justify-center' } : {})}
                    >
                      {item.image?.src && (
                        <Image
                          alt={item.alt}
                          {...(item?.imageSize
                            ? { contain: true, width: item.imageSize, height: item.imageSize }
                            : (isImageSizeEmpty
                                ? { fill: true }
                                : { contain: true, width: imageSize, height: imageSize }
                              )
                            )
                          }
                          sizes="(min-width: 768px) 25vw, 50vw"
                          src={item.image.src}
                          className="object-cover"
                        />
                      )}
                    </div>
                  </div>
                  {item?.link?.text && (
                    <h4 className="w-full text-center text-lg xl:text-xl">{item?.link?.text}</h4>
                  )}
                </li>
              ) : (
                <li 
                  className={`${style === 'horizontal' ? 'flex flex-col items-center shadow-hairline p-2 sm:py-4 sm:flex-row' : ''}`}
                  key={index}>
                  <div className={`relative ${isImageSizeEmpty ? 'bg-offWhite aspect-square' : ''}`}>
                    <Link
                      aria-label={
                        item?.link?.newTab
                          ? `Open in a new tab to view ${item?.link?.text}`
                          : item?.link?.text
                      }
                      href={item?.link?.url}
                      newTab={item?.link?.newTab}
                      type={item?.link?.type}
                      {...(!isImageSizeEmpty ? { className: 'w-full h-full flex justify-center' } : {})}
                    >
                      {item.image?.src && (
                        <Image
                          alt={item.alt}
                          {...(item?.imageSize
                            ? { contain: true, width: item.imageSize, height: item.imageSize }
                            : (isImageSizeEmpty
                                ? { fill: true }
                                : { contain: true, width: imageSize, height: imageSize }
                              )
                            )
                          }
                          sizes="(min-width: 768px) 25vw, 50vw"
                          src={item.image.src}
                        />
                      )}
                    </Link>
                  </div>
                  {item?.link?.text && (
                    <Link
                      aria-label={
                        item?.link?.newTab
                          ? `Open in a new tab to view ${item?.link?.text}`
                          : item?.link?.text
                      }
                      className="block w-full"
                      href={item?.link?.url}
                      newTab={item?.link?.newTab}
                      type={item?.link?.type}
                    >
                      <h4 className="text-center text-lg xl:text-xl">{item?.link?.text}</h4>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

AccordionPlus.displayName = 'AccordionPlus';